'use strict';
/* ==========================================================================
   levels/song.js — step "song" (any): DEEP TIME, the folk-rock lyric video (DESIGN.md §5.4).

   ► The song: assets/music/song/deep_time.m4a = ACE-Step take stomp_42 (the user's pick, 2026-09-27; CHOICE.md).
     Timings: assets/music/song/top1_words.json (tools/song_timing.py from the take's two Whisper passes): every lyric
     line of content/song.json with word times, and the beat grid {bpm 100, offset = first downbeat}.

   The whole frame is a pure function of SONG TIME t (the shared <audio> element's clock while it plays; a wall clock
   while audio is locked or missing), so seeking, pausing and the QA screenshots are exact. Every beat of the pictures
   is keyed to a sung word (S.W(line, word)), so the video follows the take's own timing.

   Acts, keyed to lyric-line indices (content/song.json order):
     intro    0–1    DEEP TIME in ransom letters over the lesson's strata, shaking on the beat
     plates   2–5    verse 1: the Atlantic spreading from its ridge (the rate label is Plates.ATLANTIC), the mantle
                     creeping as solid rock (Plates.MANTLE_FLOW_CM_YR), the cold old edge sinking and pulling
     cycle    6–9, 14–17, 24–29   the choruses: the rock-cycle wheel (magma → igneous → sedimentary → metamorphic);
                     "melt it / squeeze it / break it down" light their arrows, a grain runs round on the beat,
                     the deep-time clock spins, and "any rock can be any kind" draws the shortcuts
     sand     10–13  verse 2: granite → sand → (river, layers) → sandstone → quartzite
     magma    18–19  ONE granite-type magma: cooled fast at the surface = rhyolite (tiny crystals), slow and deep =
                     granite (big crystals). Cooling speed sets the crystal size; the magma's make-up sets the rock
                     (DESIGN.md §4.2). Names from RockCycle.COOL, crystal words from RockCycle.CRYSTALS.
     florida  20–21  shells pressed to limestone; slightly acidic rain eats it: the sinkhole
     ruler    22     one ruler (30 cm) at Plates.ATLANTIC cm a year = about a lifetime
     pangaea  23     Pangaea splitting (the video's real coastlines, a simplified sketch: GroundVideo.geo())
   Karaoke (bottom band): the current line in handwriting, sung words written on, the active word on a mustard
   scrap; the hook phrases pop as ransom letters on their word times; the next line faint below.
   Leo (left) and Dr. Quinn (right) are small cut-outs bobbing on the beat.
   Controls: click / Space play-pause, ↺ restart (R), ⌂ map, ← → seek 5 s, click or drag the progress strip.
   Portrait phone: the stage on top, Dock.karaoke + ↺ ▶ ⌂ + the strip below. The first full listen → Game.complete.
   Long music (docs/WEBSITE.md §8.4): lesson.json preload.stream names the .m4a, so it plays through the shared
   <audio> element (Audio.media), never decoded into memory.
   Drawing helpers copied (not shared) from levels/rocks.js (the code-drawn specimens) and levels/video.js (ruler).

   QA hooks: sc.song (timings: S(i), E(i), W(i, word), beats), sc.plan.acts, sc.debugAt(t) (freeze at song time t),
   sc.now(), sc.play(t), sc.pause(), sc.toggle(), sc.seek(t), sc.restart(), sc.done() (stamp now), sc.lyricAt(t),
   sc.actAt(t), sc.heard, sc.ended, sc.audioReady.
   ========================================================================== */
(function () {
  const STEP = 'song';
  const TAKE = 'top1', TAKE_NAME = 'stomp_42';
  const AUDIO = 'music/song/deep_time.m4a', WORDS = 'music/song/top1_words.json';
  const KEY = 'song:' + TAKE;
  const HANDF = "'Caveat Brush'", TYPE = "'Special Elite'";
  const COL = window.GroundLook ? GroundLook.COL : { mantle: '#d9763a', magma: '#e0533d', limestone: '#e8e0c8', sandstone: '#d8b98a', shale: '#8f8a7e', granite: '#c9b8a8', basalt: '#4a4a52', ocean: '#4f7fa3', survey: '#e3e6d3', crust: '#b98b5e' };
  const BAND_Y = 832, CUR_Y = 912, NEXT_Y = 1012, CUR_SIZE = 74, NEXT_SIZE = 44;
  const OUTLINE = 'rgba(37,60,90,0.92)';
  const WIPE = 0.6;
  const RULER_CM = 30;                                 // a school ruler (the video's "one ruler")
  const PL = () => window.Plates || { ATLANTIC: 2.2, MANTLE_FLOW_CM_YR: [2, 3], PANGAEA_SPLIT_MYR: 200 };
  const COOLED = () => (window.RockCycle && RockCycle.COOL) || { deep: { granitic: 'granite', basaltic: 'gabbro' }, surface: { granitic: 'rhyolite', basaltic: 'basalt' } };
  const CRYST = () => (window.RockCycle && RockCycle.CRYSTALS) || { deep: 'big crystals (cooled slowly)', surface: 'tiny crystals (cooled fast)' };
  const fmtNum = x => (+(+x).toFixed(1)).toLocaleString('en-US', { maximumFractionDigits: 1 });

  /* ---------------- song timing ---------------- */
  const normW = w => String(w).toLowerCase().replace(/[^a-z0-9]/g, '');
  class Song {
    constructor(d) {
      this.d = d;
      this.lines = d.lines;
      this.dur = d.duration || 120;
      const b = d.beats || {};
      this.bpm = b.bpm || 100; this.bt = 60 / this.bpm; this.off = b.offset || 0;
      this.items = d.lines.map(l => ({ ...l, kind: 'line' }))
        .concat((d.extras || []).map((x, j) => ({ ...x, i: 100 + j })))
        .sort((a, b) => a.s - b.s);
    }
    S(i) { return this.lines[i] ? this.lines[i].s : 1e6; }
    E(i) { return this.lines[i] ? this.lines[i].e : 1e6; }
    /* start of the n-th word of line i that matches `word` (exact, then prefix); the line start if none */
    W(i, word, n = 1) {
      const l = this.lines[i]; if (!l) return 1e6;
      const k = normW(word);
      for (const pass of [w => normW(w.w) === k, w => normW(w.w).startsWith(k)]) {
        let c = 0;
        for (const w of l.words) if (pass(w) && ++c === n) return w.s;
      }
      if (window.__songCueMiss) window.__songCueMiss.push(i + ':' + word);
      return l.s;
    }
    beatPos(t) { return (t - this.off) / this.bt; }
    sinceBeat(t) { const p = this.beatPos(t); return (p - Math.floor(p)) * this.bt; }
    sinceDown(t) { const p = this.beatPos(t) / 4; return (p - Math.floor(p)) * this.bt * 4; }
    pulse(t, k = 9) { return t < this.off - 0.01 ? 0 : Math.exp(-this.sinceBeat(t) * k); }
    downPulse(t, k = 7) { return t < this.off - 0.01 ? 0 : Math.exp(-this.sinceDown(t) * k); }
  }

  /* ---------------- small drawing helpers ---------------- */
  function label(str, x, y, size, t0, o = {}) { hand(str, x, y, size, t0, Object.assign({ color: PAL.cream, outline: OUTLINE, seed: 5 }, o)); }
  function tag(str, x, y, size, s = 1, o = {}) {
    if (s <= 0) return;
    g.save(); g.translate(x, y); g.rotate(o.rot ?? -0.04); g.scale(s, s);
    g.font = `${size}px ${o.font || HANDF}`;
    const w = g.measureText(str).width + size * 0.9, h = size * 1.35;
    paperRect(0, 0, w, h, o.color || PAL.cream, { seed: o.seed || 41, depth: 1.0, outline: true });
    g.fillStyle = o.ink || PAL.ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(str, 0, size * 0.05);
    g.restore();
  }
  function strike(x0, y0, x1, y1, prog, seed) { ink([[x0, y0], [(x0 + x1) / 2 + 4, (y0 + y1) / 2 - 3], [x1, y1]], { w: 9, color: PAL.red, seed, prog, passes: 2, step: 10 }); }
  const win = (t, a, b, ramp = 0.3) => Math.min(E.inOutSine(P(t, a, a + ramp)), 1 - E.inOutSine(P(t, b, b + ramp)));
  function backdrop(color, seed, tex) { g.fillStyle = paperTile(color, seed, tex); g.fillRect(0, 0, 1920, 1080); }
  function grid(alpha = 1) {
    g.save(); g.globalAlpha *= alpha; g.strokeStyle = 'rgba(96,120,92,0.16)'; g.lineWidth = 1.5; g.beginPath();
    for (let x = 60; x < 1920; x += 120) { g.moveTo(x, 0); g.lineTo(x, 1080); }
    for (let y = 60; y < 1080; y += 120) { g.moveTo(0, y); g.lineTo(1920, y); }
    g.stroke(); g.restore();
  }
  /* rain drops over a box, a pure function of t */
  function rain(t, x0, x1, y0, y1, n, alpha, seed = 1) {
    if (alpha <= 0) return;
    const r = rng(seed); g.save(); g.globalAlpha *= alpha; g.fillStyle = COL.ocean;
    for (let i = 0; i < n; i++) {
      const rx = x0 + r() * (x1 - x0), sp = 380 + r() * 160, ph = r() * (y1 - y0);
      const ry = y0 + ((t * sp + ph) % (y1 - y0));
      g.beginPath(); g.ellipse(rx, ry, 4, 10, 0, 0, TAU); g.fill();
    }
    g.restore();
  }

  /* ---------------- the specimens (copied from levels/rocks.js: engraving cut-out when IMG has it, else code-drawn) ---------------- */
  const ROCK = {
    granite: '#c9b8a8', rhyolite: '#d7b2a2', basalt: '#4a4a52', gabbro: '#3b4640', sandstone: '#d8b98a', shale: '#8f8a7e',
    limestone: '#e8e0c8', quartzite: '#efe9df', slate: '#5b6168', marble: '#f3efe7', gneiss: '#a39a8f', magma: '#e0533d',
    sand: '#e3c78f', mud: '#8a6a4a', shells: '#f1e6cf',
  };
  const kindSeed = k => 11 + Object.keys(ROCK).indexOf(k) * 7;
  const shape = (k, r) => blobPts(r, Math.round(r * (k === 'sand' || k === 'mud' ? 0.62 : 0.74)), kindSeed(k), k === 'magma' ? 0.1 : k === 'sand' || k === 'mud' ? 0.06 : 0.16, 36);
  function marks(k, r) {
    const q = rng(kindSeed(k) * 131 + r), rx = r * 0.9, ry = r * 0.7;
    const at = () => { for (;;) { const x = (q() * 2 - 1) * rx, y = (q() * 2 - 1) * ry; if ((x * x) / (rx * rx) + (y * y) / (ry * ry) <= 1) return [x, y]; } };
    const dot = (c, s) => { const [x, y] = at(); g.fillStyle = c; g.beginPath(); g.arc(x, y, s, 0, TAU); g.fill(); };
    const wave = (y, amp, c, w, ph) => { g.strokeStyle = c; g.lineWidth = w; g.beginPath(); for (let x = -r; x <= r; x += 8) { const yy = y + Math.sin(x / r * 3 + ph) * amp; if (x === -r) g.moveTo(x, yy); else g.lineTo(x, yy); } g.stroke(); };
    g.lineCap = 'round';
    switch (k) {
      case 'granite': for (let i = 0; i < 110; i++) dot(['#2b2420', '#f4ece2', '#e6a79a', '#fffdf7'][i % 4], r * (0.015 + q() * 0.035)); break;
      case 'rhyolite': for (let i = 0; i < 4; i++) wave(-ry * 0.6 + i * ry * 0.4, 6, 'rgba(150,80,65,0.45)', 3, i); for (let i = 0; i < 140; i++) dot(i % 3 ? 'rgba(255,255,255,0.7)' : '#9c5a4c', r * 0.012); break;
      case 'quartzite': for (let i = 0; i < 90; i++) dot(i % 2 ? '#fffdf7' : 'rgba(170,160,140,0.6)', r * (0.012 + q() * 0.02)); break;
      case 'magma': g.strokeStyle = '#f7c948'; g.lineWidth = 5; for (let i = 0; i < 6; i++) { let [x, y] = at(); g.beginPath(); g.moveTo(x, y); for (let j = 0; j < 4; j++) { x += (q() - 0.5) * r * 0.5; y += (q() - 0.5) * r * 0.35; g.lineTo(x, y); } g.stroke(); } for (let i = 0; i < 12; i++) dot('rgba(251,231,161,0.8)', r * 0.03); break;
      case 'basalt': for (let i = 0; i < 70; i++) dot(i % 5 ? '#2a2a30' : '#7a7a82', r * (0.01 + q() * 0.03)); break;
      case 'sandstone': for (let i = 0; i < 5; i++) wave(-ry * 0.8 + i * ry * 0.4, 4, 'rgba(150,110,60,0.55)', 4, i * 0.7); for (let i = 0; i < 90; i++) dot('rgba(120,90,50,0.5)', r * 0.012); break;
      case 'gneiss': for (let i = 0; i < 7; i++) wave(-ry + i * ry * 0.33, 10, i % 2 ? 'rgba(60,54,48,0.75)' : 'rgba(245,238,228,0.9)', r * 0.1, i * 0.4); break;
      case 'sand': for (let i = 0; i < 160; i++) dot(i % 3 ? 'rgba(160,120,60,0.6)' : '#fffdf7', r * (0.012 + q() * 0.015)); break;
      default: break;
    }
  }
  const markSlots = new Map();
  function specimen(k, x, y, r, { sc = 1, alpha = 1, rot = 0, depth = 1.3 } = {}) {
    if (sc <= 0.001 || alpha <= 0) return;
    if (IMG[k]) { cut(k, x, y, r * 1.75, { s: sc, alpha, rot, depth, boil: false, seed: kindSeed(k) }); return; }
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(sc, sc); g.globalAlpha *= alpha;
    const pts = shape(k, r), bs = 1 + 14 / r;
    g.save(); g.scale(bs, bs); paperPiece(pts, 0, 0, '#fbf5e6', { seed: kindSeed(k) + 1, depth: depth / bs, boil: false }); g.restore();
    paperPiece(pts, 0, 0, ROCK[k] || PAL.kraft, { seed: kindSeed(k), depth: 0, boil: false });
    const body = () => { g.save(); pathPts(pts); g.clip(); marks(k, r); pathPts(pts); g.strokeStyle = 'rgba(43,36,32,0.6)'; g.lineWidth = 5; g.stroke(); g.restore(); };
    let slot = markSlots.get(k + '|' + r); if (!slot) markSlots.set(k + '|' + r, slot = {});
    if (!(FX.sprites && _ctm() && spriteIn(slot, -r * 1.25, -r * 1.25, r * 2.5, r * 2.5, body))) body();
    g.restore();
  }
  /* a soft glow (no per-frame blur): concentric translucent discs */
  function glow(x, y, r, a, color = '255,170,60') {
    if (a <= 0) return;
    g.save();
    for (let i = 4; i >= 1; i--) { g.fillStyle = `rgba(${color},${(0.10 * a).toFixed(3)})`; g.beginPath(); g.arc(x, y, r * (0.7 + i * 0.22), 0, TAU); g.fill(); }
    g.restore();
  }
  /* the school ruler (copied from levels/video.js) */
  function ruler(x, y, L, rot = 0, { s = 1, seed = 140, mark = null, alpha = 1 } = {}) {
    if (s <= 0 || alpha <= 0) return;
    const h = Math.max(14, L * 0.085);
    g.save(); g.globalAlpha *= alpha; g.translate(x, y); g.rotate(rot); g.scale(s, s);
    paperRect(0, 0, L, h, '#f2d27a', { seed, depth: 1.0, outline: true });
    g.strokeStyle = 'rgba(43,36,32,0.8)'; g.lineWidth = Math.max(1, L / 520); g.beginPath();
    for (let cm = 0; cm <= RULER_CM; cm++) { const px = -L / 2 + L * 0.03 + (L * 0.94) * cm / RULER_CM, tl = cm % 10 === 0 ? h * 0.55 : cm % 5 === 0 ? h * 0.4 : h * 0.25; g.moveTo(px, -h / 2); g.lineTo(px, -h / 2 + tl); }
    g.stroke();
    if (L > 300) {
      g.fillStyle = PAL.ink; g.font = `${Math.round(h * 0.34)}px ${TYPE}`; g.textAlign = 'center'; g.textBaseline = 'middle';
      for (let cm = 0; cm <= RULER_CM; cm += 5) g.fillText(String(cm), -L / 2 + L * 0.03 + (L * 0.94) * cm / RULER_CM, h * 0.18);
    }
    if (mark != null) {
      const px = -L / 2 + L * 0.03 + (L * 0.94) * mark / RULER_CM;
      g.fillStyle = 'rgba(224,83,61,0.35)'; g.fillRect(-L / 2 + L * 0.03, -h / 2 + 2, px + L / 2 - L * 0.03, h - 4);
      ink([[px, -h / 2 - 12], [px, h / 2 + 12]], { w: 5, color: PAL.red, seed: seed + 3, step: 8 });
    }
    g.restore();
  }

  /* ======================================================================
     act: intro (lines 0–1)
     ====================================================================== */
  function actIntro(S, t, A) {
    backdrop(PAL.kraft, 3, 'tex_kraft');
    // the strata shake on each beat while "the ground is moving"
    const shake = t > S.W(0, 'moving') - 0.1 ? S.pulse(t, 8) : 0.25 * S.pulse(t, 8);
    g.save(); g.translate(Math.sin(t * 41) * 7 * shake, Math.cos(t * 37) * 5 * shake);
    if (window.GroundLook) GroundLook.strata(520, 330, { seed: 7 });
    g.restore();
    const ex = A.end - 0.45;
    ransom('DEEP TIME', 960, 230, 150, 0.05, { seed: 401, exitT: ex });
    if (t < ex + 0.3) label('a folk-rock song about rocks that will not sit still', 960, 380, 52, 0.7, { seed: 402, alpha: 1 - P(t, ex, ex + 0.3) });
    // "and so are you": a pin on the ground, riding the shake
    const tY = S.W(1, 'you');
    if (t > tY) {
      const s = pop(t, tY, 0.35);
      g.save(); g.translate(Math.sin(t * 41) * 7 * shake, 0);
      paperCircle(960, 500, 16 * s, PAL.red, { seed: 403, depth: 0.8 });
      ink([[960, 516], [960, 560]], { w: 5, color: PAL.ink, seed: 404, step: 8, prog: s > 0.5 ? 1 : 0 });
      g.restore();
      tag('you (also moving)', 960, 440, 40, s, { color: PAL.mustard, seed: 405, rot: 0.03 });
    }
  }

  /* ======================================================================
     act: plates (verse 1, lines 2–5): a cross-section of a spreading ocean and a sinking edge
     ====================================================================== */
  const SEA = 300, PT = 380, PTH = 62, RIDGE = 1000, TRENCH = 470, V = 20;       // V: drawn spreading speed (px/s, sped up)
  const SLAB_END = [300, 780];
  function actPlates(S, t, A) {
    const tau = t - A.at;
    // sky, sea, mantle
    g.fillStyle = paperTile('#cfe4ea', 3); g.fillRect(0, 0, 1920, SEA);
    g.fillStyle = paperTile('#9cc0d2', 4); g.fillRect(0, SEA, 1920, PT - SEA + 4);
    g.fillStyle = paperTile(COL.mantle, 5); g.fillRect(0, PT + PTH - 4, 1920, 1080);
    // mantle creep: two slow convection cells (dashes creep along; rising under the ridge)
    const tM = S.W(4, 'mantle'), ma = 0.25 + 0.75 * P(t, tM - 0.2, tM + 0.4);
    g.save(); g.lineWidth = 7; g.strokeStyle = `rgba(120,40,20,${(0.55 * ma).toFixed(3)})`; g.setLineDash([26, 22]); g.lineCap = 'round';
    [[720, 640, false], [1300, 640, true]].forEach(([cx, cy, cw], i) => {
      g.lineDashOffset = -tau * 14;
      g.beginPath(); g.ellipse(cx, cy, 250, 110, 0, 0, TAU, !cw); g.stroke();
    });
    g.restore();
    // the overriding plate (a continent) at the left: the ocean plate dives under it at the trench
    paperRect(TRENCH / 2 - 20, PT + 16, TRENCH + 40, PTH + 70, COL.crust, { seed: 409, torn: true, depth: 1.2, tex: 'tex_kraft', boil: false });
    // the ridge: hot rock welling up, pulsing on the beat
    const pu = S.pulse(t, 6);
    glow(RIDGE, PT + 50, 70 + 12 * pu, 0.9);
    paperPiece([[-60, 90], [0, -40], [60, 90]], RIDGE, PT + 40, COL.magma, { seed: 410, depth: 0.4, boil: false });
    // right plate (moves right) and left plate (moves left, bends down at the trench)
    g.fillStyle = paperTile('#6d6a66', 6); g.fillRect(RIDGE + 4, PT, 1920 - RIDGE, PTH);
    g.fillRect(TRENCH, PT, RIDGE - 4 - TRENCH, PTH);
    const slab = [[TRENCH, PT], [TRENCH + 30, PT + PTH], [SLAB_END[0] + 40, SLAB_END[1]], [SLAB_END[0] - 20, SLAB_END[1] - 30]];
    pathPts(slab); g.fill();
    // age stripes riding away from the ridge (new rock is made at the ridge)
    const o = (V * tau) % 90;
    g.fillStyle = 'rgba(245,238,228,0.35)';
    for (let x = RIDGE + 12 + o - 90; x < 1920; x += 90) if (x > RIDGE + 6) g.fillRect(x, PT + 4, 26, PTH - 8);
    for (let x = RIDGE - 38 - o + 90; x > TRENCH; x -= 90) if (x < RIDGE - 32) g.fillRect(Math.max(TRENCH, x), PT + 4, 26, PTH - 8);
    g.strokeStyle = 'rgba(43,36,32,0.7)'; g.lineWidth = 3;
    g.strokeRect(RIDGE + 4, PT, 1920 - RIDGE, PTH); g.beginPath(); g.moveTo(RIDGE - 4, PT); g.lineTo(TRENCH, PT); pathPts(slab, false); g.moveTo(TRENCH + 30, PT + PTH); g.lineTo(RIDGE - 4, PT + PTH); g.stroke();
    // a continent rides the right plate ("we're going for a ride")
    const cx = 1230 + V * tau;
    paperRect(cx, PT - 22, 250, 60, COL.crust, { seed: 411, torn: true, depth: 1.2, tex: 'tex_kraft' });
    const tR = S.W(3, 'ride');
    if (t > S.W(3, 'plates')) {
      paperCircle(cx, PT - 104, 14, PAL.red, { seed: 412, depth: 0.8 });
      ink([[cx, PT - 90], [cx, PT - 52]], { w: 5, seed: 413, step: 8 });
      if (t > tR) tag('along for the ride', cx, PT - 160, 36, pop(t, tR, 0.35), { color: PAL.mustard, seed: 414 });
    }
    // line 2: the ocean gets wider: markers ride the plates apart
    const t2 = S.W(2, 'Two'), tWide = S.W(2, 'wide');
    if (t > t2) {
      const d = 40 + V * (t - t2), wa = 1 - P(t, S.S(4) - 0.3, S.S(4) + 0.2);
      if (wa > 0) {
        g.save(); g.globalAlpha *= wa;
        [RIDGE - d, RIDGE + d].forEach((x, i) => paperRect(x, PT - 10, 12, 36, PAL.white, { seed: 420 + i, depth: 0.5 }));
        if (t > tWide) inkArrow(RIDGE, SEA - 30, RIDGE + d, SEA - 30, { w: 5, color: PAL.navy, seed: 422, head: 18 });
        if (t > tWide) inkArrow(RIDGE, SEA - 30, RIDGE - d, SEA - 30, { w: 5, color: PAL.navy, seed: 423, head: 18 });
        g.restore();
      }
      tag('the ocean floor spreads about ' + Math.round(PL().ATLANTIC) + ' cm a year', RIDGE, 170, 42, pop(t, t2, 0.35), { color: PAL.white, seed: 424 });
      if (t > tWide) label('(sped up a LOT)', RIDGE, 240, 34, tWide, { seed: 425, color: PAL.navy, outline: null });
    }
    // line 4: the mantle is not lava, it is solid rock that creeps
    const tL = S.W(4, 'lava'), tSo = S.W(4, 'solid'), tC = S.W(4, 'creeps');
    if (t > tL) {
      tag('lava?', 860, 600, 48, pop(t, tL, 0.35), { color: PAL.red, ink: PAL.cream, seed: 430, rot: -0.08 });
      if (t > tSo) strike(790, 625, 930, 575, P(t, tSo, tSo + 0.3), 431);
    }
    if (t > tSo) tag('the mantle: solid rock', 1300, 560, 44, pop(t, tSo, 0.35), { color: PAL.cream, seed: 432 });
    if (t > tC) {
      const [a, b] = PL().MANTLE_FLOW_CM_YR;
      tag('it creeps ' + a + ' to ' + b + ' cm a year', 1300, 640, 40, pop(t, tC, 0.35), { color: PAL.mustard, seed: 433, rot: 0.03 });
    }
    // line 5: the cold old edge sinks and pulls the plate
    const tCo = S.W(5, 'cold'), tSi = S.W(5, 'sinking'), tPu = S.W(5, 'pulling');
    if (t > tCo) tag('cold, heavy old edge', 660, 500, 40, pop(t, tCo, 0.35), { color: PAL.white, seed: 440, rot: -0.03 });
    if (t > tSi) paperArrow(TRENCH - 20, PT + 120, 230 * E.outCubic(P(t, tSi, tSi + 0.5)), 30, PAL.navy, { rot: Math.atan2(SLAB_END[1] - PT, SLAB_END[0] - TRENCH), seed: 441 });
    if (t > tPu) {
      paperArrow(900, PT + PTH / 2, 280 * E.outCubic(P(t, tPu, tPu + 0.5)), 26, PAL.red, { rot: Math.PI, seed: 442 });
      tag('sinking edge pulls the plate', 720, 760, 40, pop(t, tPu, 0.35), { color: PAL.mustard, seed: 443, rot: -0.02 });
    }
  }

  /* ======================================================================
     act: the rock-cycle wheel (the choruses, lines 6–9 / 14–17 / 24–29)
     ====================================================================== */
  const WC = { x: 960, y: 450, R: 245, r: 66 };
  const NODES = [      // clockwise from the top; angle on the wheel
    { k: 'magma', a: -Math.PI / 2, name: 'magma' },
    { k: 'granite', a: 0, name: 'igneous (granite)' },
    { k: 'sandstone', a: Math.PI / 2, name: 'sedimentary (sandstone)' },
    { k: 'gneiss', a: Math.PI, name: 'metamorphic (gneiss)' },
  ];
  const ARROWS = [     // i → i+1 round the wheel; `word` = the hook word that lights it
    { label: 'cool it', word: null },
    { label: 'break it down', word: 'break' },
    { label: 'squeeze it', word: 'squeeze' },
    { label: 'melt it', word: 'Melt' },
  ];
  const SHORTCUTS = [[1, 3, 'squeeze'], [2, 0, 'melt'], [3, 2, 'break down'], [1, 0, 'melt']];
  const nodeXY = (i, R = WC.R) => [WC.x + Math.cos(NODES[i].a) * R, WC.y + Math.sin(NODES[i].a) * R];
  function arcArrow(a0, a1, prog, o) {
    if (prog <= 0) return;
    const pts = []; for (let i = 0; i <= 18; i++) { const a = lerp(a0, a1, i / 18); pts.push([WC.x + Math.cos(a) * WC.R, WC.y + Math.sin(a) * WC.R]); }
    ink(pts, { w: o.w, color: o.color, seed: o.seed, prog: clamp(prog * 1.2), step: 12 });
    const hp = clamp((prog - 0.8) / 0.2);
    if (hp > 0) {
      const [x2, y2] = pts[pts.length - 1], ang = a1 + Math.PI / 2, hd = 24;
      ink([[x2 - Math.cos(ang - 0.45) * hd, y2 - Math.sin(ang - 0.45) * hd], [x2, y2], [x2 - Math.cos(ang + 0.45) * hd, y2 - Math.sin(ang + 0.45) * hd]], { w: o.w, color: o.color, seed: o.seed + 5, prog: hp, step: 8 });
    }
  }
  function clock(x, y, r, spin, s) {
    if (s <= 0) return;
    g.save(); g.translate(x, y); g.scale(s, s);
    paperCircle(0, 0, r, PAL.cream, { seed: 460, depth: 1.3, outline: true });
    for (let k = 0; k < 12; k++) { const a = k / 12 * TAU; ink([[Math.cos(a) * r * 0.78, Math.sin(a) * r * 0.78], [Math.cos(a) * r * 0.92, Math.sin(a) * r * 0.92]], { w: 3, seed: 461 + k, step: 10, passes: 1 }); }
    const a = -Math.PI / 2 + spin;
    ink([[0, 0], [Math.cos(a) * r * 0.72, Math.sin(a) * r * 0.72]], { w: 5, color: PAL.red, seed: 475, step: 10 });
    const h = -Math.PI / 2 + spin / 12;
    ink([[0, 0], [Math.cos(h) * r * 0.46, Math.sin(h) * r * 0.46]], { w: 7, seed: 476, step: 10 });
    g.restore();
  }
  function actCycle(S, t, A) {
    const c0 = A.c0;
    backdrop(PAL.kraft, 4, 'tex_kraft');
    const dp = S.downPulse(t, 6), inS = pop(t, A.at + 0.1, 0.45);
    // the wheel's track
    g.save(); g.globalAlpha *= 0.5 * clamp(inS); g.setLineDash([10, 14]); g.strokeStyle = 'rgba(43,36,32,0.5)'; g.lineWidth = 4;
    g.beginPath(); g.arc(WC.x, WC.y, WC.R, 0, TAU); g.stroke(); g.restore();
    // arrows round the wheel: always there in pencil; the hook word inks it red
    ARROWS.forEach((ar, i) => {
      const a0 = NODES[i].a + 0.36, a1 = NODES[i].a + Math.PI / 2 - 0.36;
      const tw = ar.word ? S.W(c0, ar.word) : A.at + 0.3;
      arcArrow(a0, a1, clamp(inS), { w: 4, color: 'rgba(43,36,32,0.45)', seed: 480 + i });
      const hot = ar.word && t > tw;
      if (hot) arcArrow(a0, a1, P(t, tw, tw + 0.45), { w: 9, color: PAL.red, seed: 485 + i });
      const am = (a0 + a1) / 2, lx = WC.x + Math.cos(am) * (WC.R + 108), ly = WC.y + Math.sin(am) * (WC.R + 70);
      const s = (ar.word ? (t > tw ? 1 + 0.25 * Math.max(0, spring(t, tw, 2.2, 5)) : 0.85) : 0.85) * clamp(inS);
      tag(ar.label, lx, ly, ar.word ? 40 : 34, s, { color: hot ? PAL.red : PAL.cream, ink: hot ? PAL.cream : PAL.ink, seed: 490 + i, rot: (i % 2 ? 0.05 : -0.05) });
    });
    // "any rock can be any kind": the shortcuts across the wheel
    const tAny = S.W(c0 + 3, 'Any');
    if (t > tAny - 0.05) SHORTCUTS.forEach(([a, b], j) => {
      const [x1, y1] = nodeXY(a, WC.R - WC.r - 18), [x2, y2] = nodeXY(b, WC.R - WC.r - 18);
      inkArrow(x1, y1, x2, y2, { bulge: j % 2 ? -250 : 250, w: 6, color: PAL.teal, seed: 500 + j, head: 20, prog: P(t, tAny + j * 0.25, tAny + j * 0.25 + 0.5) });
    });
    // the nodes pulse on the downbeat
    NODES.forEach((n, i) => {
      const [x, y] = nodeXY(i), s = clamp(inS) * (1 + 0.05 * dp);
      if (n.k === 'magma') glow(x, y, WC.r * 1.25, 0.7 * s);
      specimen(n.k, x, y, WC.r, { sc: s, rot: 0.05 * Math.sin(i * 2.1) });
      const ly = i === 0 ? y - WC.r - 40 : y + WC.r + 36, lx = x + (i === 1 ? 50 : i === 3 ? -50 : 0);
      hand(n.name, lx, ly, 36, A.at + 0.3 + i * 0.1, { color: PAL.cream, outline: OUTLINE, seed: 510 + i });
    });
    // "rocks keep cycling all around": a grain runs round the wheel, one lap every four bars
    const tCy = S.W(c0 + 1, 'Rocks');
    if (t > tCy) {
      const a = -Math.PI / 2 + (S.beatPos(t) - S.beatPos(tCy)) / 16 * TAU, pu = S.pulse(t, 8);
      paperCircle(WC.x + Math.cos(a) * WC.R, WC.y + Math.sin(a) * WC.R, 17 + 5 * pu, PAL.mustard, { seed: 520, depth: 0.9, outline: true });
    }
    // "give it time, give it deep, deep time": the clock spins (faster on "deep")
    const tG = S.W(c0 + 2, 'Give'), tD = S.W(c0 + 2, 'deep');
    if (t > tG - 0.1) {
      const spin = (t - tG) * 3 + Math.max(0, t - tD) * 9;
      clock(WC.x, WC.y, 76, spin, pop(t, tG - 0.1, 0.4));
      if (t > tD) label('millions of years', WC.x, WC.y + 108, 32, tD, { seed: 530 });
    }
    if (A.finale) {
      // the outro: "any rock can be any kind" once more, then the stamp
      // the outro: the wheel steps back under a paper wash; the take-away, then the stamp
      const tOut = S.S(28) - 0.3, tEnd = S.E(S.lines.length - 1);
      if (t > tOut) {
        g.save(); g.fillStyle = `rgba(233,220,190,${(0.6 * P(t, tOut, tOut + 0.6)).toFixed(3)})`; g.fillRect(0, 0, 1920, BAND_Y + 20); g.restore();
        tag('any rock can become any other kind', WC.x, 300, 60, pop(t, tOut + 0.2, 0.4), { color: PAL.mustard, seed: 540, rot: -0.02 });
      }
      if (t > tEnd + 0.2) UI.stamp('DEEP TIME', WC.x, 560, { s: 1.2 * pop(t, tEnd + 0.2, 0.35), rot: -0.08 });
    }
  }

  /* ======================================================================
     act: sand (verse 2, lines 10–13): granite → sand → layers → sandstone → quartzite
     ====================================================================== */
  const SX = [390, 770, 1150, 1530], SY = 330, SR = 82;
  function actSand(S, t, A) {
    backdrop(COL.survey, 6); grid(1);
    const tG = S.W(10, 'granite'), tRn = S.W(10, 'Rain'), tCr = S.W(10, 'crumbles'), tSa = S.W(10, 'sand');
    const tRv = S.W(11, 'river'), tSe = S.W(11, 'seaward'), tSt = S.W(11, 'settles');
    const tBu = S.W(12, 'Buried'), tPr = S.W(12, 'pressed'), tCe = S.W(12, 'cemented'), tSs = S.W(12, 'sandstone');
    const tDp = S.W(13, 'deep'), tHt = S.W(13, 'heat'), tQz = S.W(13, 'quartzite');
    // the landscape strip below: a river runs to the sea, where the layers pile up
    const LY = 650;
    paperRect(700, LY + 60, 1000, 170, '#b7a27a', { seed: 600, torn: true, depth: 1.0, tex: 'tex_kraft', boil: false });
    paperRect(1430, LY + 70, 420, 170, '#8fb5c7', { seed: 601, torn: true, depth: 1.0, boil: false });
    const river = [[330, LY - 10], [520, LY + 30], [760, LY + 10], [980, LY + 50], [1220, LY + 40]];
    ink(river, { w: 26, color: '#7fa9c2', seed: 602, step: 14, passes: 1, prog: P(t, A.at, A.at + 0.8) });
    hand('the sea', 1430, LY + 10, 36, A.at + 0.5, { color: PAL.navy, seed: 603, font: TYPE });
    // grains ride the river (from "river"), settle as layers (from "settles"), get pressed (from "pressed")
    if (t > tRv) {
      const r = rng(604), n = 16;
      for (let i = 0; i < n; i++) {
        const ph = r(), sp = 0.25 + r() * 0.1, u = ((t - tRv) * sp + ph) % 1;
        const seg = u * (river.length - 1), k = Math.min(river.length - 2, Math.floor(seg)), f = seg - k;
        const x = lerp(river[k][0], river[k + 1][0], f), y = lerp(river[k][1], river[k + 1][1], f) + Math.sin(u * 30 + i) * 4;
        g.fillStyle = i % 3 ? '#caa76a' : '#f3e2bd'; g.beginPath(); g.arc(x, y, 7, 0, TAU); g.fill();
      }
    }
    const layers = P(t, tSt - 0.2, tBu + 0.4), squeeze = E.inOutCubic(P(t, tPr, tCe + 0.3));
    if (layers > 0) {
      const cols = ['#e3c78f', '#d8b98a', '#c9a878', '#e3c78f'];
      for (let i = 0; i < 4; i++) {
        const li = clamp(layers * 4 - i); if (li <= 0) continue;
        const h = (26 - 8 * squeeze) * li, y = LY + 140 - i * (26 - 8 * squeeze);
        paperRect(1430, y - h / 2, 380, h, squeeze > 0.5 ? COL.sandstone : cols[i], { seed: 610 + i, torn: true, depth: 0.5, boil: false });
      }
      if (t > tPr && t < tSs + 1.2) [1330, 1530].forEach((x, i) => paperArrow(x, LY - 20, 70 * (1 - P(t, tSs + 0.8, tSs + 1.2)), 26, PAL.red, { rot: Math.PI / 2, seed: 615 + i }));
      if (t > tBu) tag('buried and pressed', 1430, LY - 90, 36, pop(t, tBu, 0.35) * (1 - P(t, tSs + 0.8, tSs + 1.2)), { color: PAL.white, seed: 617 });
      if (t > tCe) hand('(grains cemented together)', SX[2], SY + SR + 95, 30, tCe, { color: PAL.ink, seed: 618 });
    }
    // the specimens across the top
    const crumble = P(t, tCr, tCr + 0.8);
    rain(t, SX[0] - 140, SX[0] + 140, 120, SY + 20, 18, win(t, tRn - 0.2, tSa + 0.6), 620);
    if (t > S.W(10, 'ice')) tag('ice', SX[0] + 150, 150, 34, pop(t, S.W(10, 'ice'), 0.3) * (1 - P(t, tSa + 0.6, tSa + 0.9)), { color: '#dff1f7', seed: 621 });
    specimen('granite', SX[0] + (crumble > 0 && crumble < 1 ? Math.sin(t * 60) * 4 : 0), SY, SR, { sc: pop(t, tG - 0.3, 0.4) * (1 - 0.18 * crumble) });
    if (t > tCr) { ink([[SX[0] - 30, SY - 50], [SX[0] - 5, SY - 10], [SX[0] - 20, SY + 30]], { w: 4, seed: 622, prog: crumble, step: 8 }); ink([[SX[0] + 35, SY - 40], [SX[0] + 10, SY + 5], [SX[0] + 30, SY + 45]], { w: 4, seed: 623, prog: crumble, step: 8 }); }
    hand('granite', SX[0], SY + SR + 50, 40, tG, { color: PAL.navy, seed: 624 });
    specimen('sand', SX[1], SY + 10, SR * 0.85, { sc: pop(t, tSa, 0.4) });
    if (t > tSa) hand('sand', SX[1], SY + SR + 50, 40, tSa, { color: PAL.navy, seed: 625 });
    specimen('sandstone', SX[2], SY, SR, { sc: pop(t, tSs, 0.4) });
    if (t > tSs) hand('sandstone', SX[2], SY + SR + 50, 40, tSs, { color: PAL.navy, seed: 626 });
    const hot = P(t, tHt, tHt + 0.5) * (1 - P(t, tQz + 1.2, tQz + 2));
    glow(SX[3], SY, SR * 1.3, hot);
    specimen('quartzite', SX[3], SY, SR, { sc: pop(t, tQz, 0.4) });
    if (t > tQz) hand('quartzite', SX[3], SY + SR + 50, 40, tQz, { color: PAL.navy, seed: 627 });
    // the arrows between them, each inked on its word
    const arrows = [[0, tCr, 'weathered'], [1, tCe, 'cemented'], [2, tHt, 'heat + pressure']];
    arrows.forEach(([i, tw, lb], j) => {
      if (t < tw) return;
      inkArrow(SX[i] + SR + 14, SY - 60, SX[i + 1] - SR - 14, SY - 60, { bulge: -40, w: 6, color: PAL.navy, seed: 630 + j, head: 20, prog: P(t, tw, tw + 0.45) });
      hand(lb, (SX[i] + SX[i + 1]) / 2, SY - 150, 36, tw + 0.1, { color: PAL.ink, seed: 633 + j });
    });
    if (t > tDp && t < tQz) label('push it deep', SX[3], SY - 150, 38, tDp, { seed: 636, color: PAL.red, outline: 'rgba(251,245,230,0.95)' });
    if (t > tSe) tag('seaward', 760, LY + 100, 34, pop(t, tSe, 0.3), { color: PAL.cream, seed: 637, rot: -0.03 });
  }

  /* ======================================================================
     act: magma (verse 3, lines 18–19): melt it, then cool it fast (tiny crystals) or slow (big crystals)
     ====================================================================== */
  const MX = 960, MY = 250;
  function crystals(x, y, r, size, seed, colors, s = 1) {
    if (s <= 0) return;
    g.save(); g.translate(x, y); g.scale(s, s);
    paperCircle(0, 0, r + 12, PAL.white, { seed, depth: 1.2 });
    g.beginPath(); g.arc(0, 0, r, 0, TAU); g.clip();
    const q = rng(seed + 1), n = Math.ceil(2 * r / size) + 1;
    for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) {
      const cx = -r + (i + 0.5) * size + (q() - 0.5) * size * 0.3, cy = -r + (j + 0.5) * size + (q() - 0.5) * size * 0.3;
      g.save(); g.translate(cx, cy); g.rotate(q() * 3); g.fillStyle = colors[Math.floor(q() * colors.length)];
      g.fillRect(-size * 0.52, -size * 0.42, size * 1.04, size * 0.84); g.restore();
    }
    g.restore();
    g.save(); g.strokeStyle = 'rgba(43,36,32,0.7)'; g.lineWidth = 4; g.beginPath(); g.arc(x, y, r * s, 0, TAU); g.stroke(); g.restore();
  }
  function actMagma(S, t, A) {
    backdrop('#2e2a3a', 7);
    g.fillStyle = paperTile(COL.mantle, 8); g.fillRect(0, 560, 1920, 520);
    const tDe = S.W(18, 'deeper'), tMe = S.W(18, 'melt'), tFl = S.W(18, 'flow'), tGl = S.W(18, 'glowing');
    const tFa = S.W(19, 'fast'), tSm = S.W(19, 'small'), tSl = S.W(19, 'slow'), tGr = S.W(19, 'grow');
    // a rock sinks ("take it deeper"), then melts into the magma blob
    const sink = E.inOutCubic(P(t, tDe - 0.4, tMe)), melt = P(t, tMe, tMe + 0.7);
    if (melt < 1) specimen('gneiss', MX, lerp(120, MY, sink), 80, { alpha: 1 - melt, sc: 1 - 0.3 * melt });
    const pu = S.pulse(t, 6);
    if (t > tMe) {
      glow(MX, MY, 130 + 14 * pu, P(t, tMe, tGl + 0.3));
      specimen('magma', MX, MY, 95, { sc: pop(t, tMe, 0.45) * (1 + 0.04 * pu) });
      label('granite-type magma', MX, MY + 120, 42, tMe + 0.2, { seed: 700 });
    }
    // "a glowing magma flow": two branches, up to the surface (left) and staying deep (right)
    if (t > tFl) {
      inkArrow(MX - 110, MY + 40, 540, 400, { bulge: 60, w: 8, color: '#f29a3a', seed: 701, head: 22, prog: P(t, tFl, tFl + 0.6) });
      inkArrow(MX + 110, MY + 40, 1380, 400, { bulge: -60, w: 8, color: '#f29a3a', seed: 702, head: 22, prog: P(t, tFl, tFl + 0.6) });
      label('erupts: cools at the surface', 470, 300, 36, tFl + 0.3, { seed: 703, color: PAL.cream });
      label('stays deep underground', 1450, 300, 36, tFl + 0.3, { seed: 704, color: PAL.cream });
    }
    // cool it fast (at the surface): rhyolite, tiny crystals
    const C = CRYST(), fastRock = COOLED().surface.granitic, slowRock = COOLED().deep.granitic;   // rhyolite / granite
    if (t > tFa - 0.1) {
      specimen(fastRock, 500, 480, 78, { sc: pop(t, tFa - 0.1, 0.4) });
      label(fastRock, 500, 580, 40, tFa, { seed: 713 });
      tag('cool it fast', 500, 640, 38, pop(t, tFa, 0.35), { color: PAL.sky, seed: 710 });
      crystals(740, 500, 70, 9, 711, ['#d7b2a2', '#c79a8a', '#f4ece2', '#9c5a4c', '#e6cfc4'], pop(t, tSm - 0.1, 0.4));
      if (t > tSm) tag(C.surface, 660, 730, 34, pop(t, tSm, 0.35), { color: PAL.white, seed: 712, rot: 0.02 });
    }
    // cool it slow: granite, big crystals that grow
    if (t > tSl - 0.1) {
      specimen(slowRock, 1420, 480, 78, { sc: pop(t, tSl - 0.1, 0.4) });
      label(slowRock, 1420, 580, 40, tSl, { seed: 723 });
      tag('cool it slow', 1420, 640, 38, pop(t, tSl, 0.35), { color: PAL.mustard, seed: 720 });
      const size = lerp(10, 38, E.outCubic(P(t, tSl, tGr + 1.2)));
      crystals(1180, 500, 70, Math.round(size / 2) * 2, 721, ['#2b2420', '#f4ece2', '#e6a79a', '#fffdf7', '#c9b8a8'], pop(t, tSl, 0.4));
      if (t > tGr) tag(C.deep, 1260, 730, 34, pop(t, tGr, 0.35), { color: PAL.white, seed: 722, rot: -0.02 });
    }
  }

  /* ======================================================================
     act: florida (lines 20–21): seashells → limestone; slightly acidic rain → the sinkhole
     ====================================================================== */
  const SK = { x: 1250, y: 520, w: 680, h: 460 };
  const CAVITY = blobPts(150, 90, 810, 0.08, 40);
  function actFlorida(S, t, A) {
    backdrop(COL.survey, 9); grid(1);
    const tFl = S.W(20, 'Florida'), tSh = S.W(20, 'seashells'), tPr = S.W(20, 'pressed'), tLi = S.W(20, 'limestone');
    const tRa = S.W(21, 'rain'), tAc = S.W(21, 'acid'), tEa = S.W(21, 'eats'), tSi = S.W(21, 'out');
    tag('FLORIDA', 560, 170, 56, pop(t, tFl - 0.1, 0.4), { color: PAL.mustard, font: TYPE, seed: 800, rot: -0.04 });
    specimen('shells', 420, 380, 88, { sc: pop(t, tSh, 0.4) });
    if (t > tSh) hand('old seashells', 420, 490, 38, tSh, { color: PAL.navy, seed: 801 });
    if (t > tPr) inkArrow(530, 380, 640, 380, { w: 6, color: PAL.navy, seed: 802, head: 20, prog: P(t, tPr, tPr + 0.4) });
    if (t > tPr) hand('pressed', 585, 330, 32, tPr, { seed: 803 });
    specimen('limestone', 740, 380, 88, { sc: pop(t, tLi, 0.4) });
    if (t > tLi) hand('limestone', 740, 490, 38, tLi, { color: PAL.navy, seed: 804 });
    if (t > tLi) label("under your feet in Florida", 580, 580, 38, tLi + 0.3, { seed: 805, color: PAL.ink, outline: null });
    // the sinkhole cross-section
    const { x, y, w, h } = SK, surf = y - h / 2 + 120, sIn = pop(t, A.at + 0.1, 0.4);
    if (sIn <= 0) return;
    paperRect(x, y, w + 30, h + 30, PAL.white, { seed: 809, depth: 1.5 });
    g.save(); g.beginPath(); g.rect(x - w / 2, y - h / 2, w, h); g.clip();
    g.fillStyle = paperTile('#cfe4ea', 3); g.fillRect(x - w / 2, y - h / 2, w, 140);
    paperRect(x, surf + (h - 120) / 2 + 10, w + 40, h - 120 + 20, COL.limestone, { seed: 811, torn: true, depth: 1.2, boil: false });
    const grow = E.outCubic(P(t, tAc, tSi)), cx = x + 40, cy = surf + 150;
    if (grow > 0) paperPiece(CAVITY, cx, cy, '#6b6358', { s: 0.15 + 0.85 * grow, seed: 814, depth: 0.2, boil: false });
    const drop = E.inQuad(P(t, tSi, tSi + 0.6));
    paperRect(x - 250, surf - 12, w / 2 - 20, 40, '#e2c48e', { seed: 815, torn: true, depth: 0.8, tex: 'tex_kraft', boil: false });
    paperRect(x + 300, surf - 12, w / 2 - 250, 40, '#e2c48e', { seed: 816, torn: true, depth: 0.8, tex: 'tex_kraft', boil: false });
    paperRect(cx, surf - 12 + drop * 140, 300, 40, '#e2c48e', { seed: 817, torn: true, depth: 0.8, tex: 'tex_kraft', rot: drop * 0.25, boil: false });
    rain(t, x - w / 2 + 20, x + w / 2 - 20, y - h / 2, surf - 20, 22, P(t, tRa - 0.2, tRa + 0.3), 818);
    g.restore();
    if (t > tAc) tag('rain + carbon dioxide: slightly acidic', x, y - h / 2 - 30, 36, pop(t, tAc, 0.35), { color: PAL.white, seed: 820 });
    if (t > tEa) label('it dissolves the limestone', x - 170, y + h / 2 - 50, 36, tEa, { seed: 821, color: PAL.navy, outline: 'rgba(251,245,230,0.9)' });
    if (t > tSi + 0.3) UI.stamp('SINKHOLE', x + 150, surf + 300, { s: 0.6 * pop(t, tSi + 0.3, 0.3), rot: 0.08 });
  }

  /* ======================================================================
     act: ruler (line 22): one ruler in a lifetime
     ====================================================================== */
  function actRuler(S, t, A) {
    backdrop(COL.survey, 10); grid(1);
    const tO = S.W(22, 'ruler'), tL = S.W(22, 'lifetime'), tG = S.W(22, 'that'), e = S.E(22);
    const L = 1300, x = 960, y = 400;
    // two coasts drift apart along the ruler as the line is sung
    const cm = RULER_CM * E.inOutSine(P(t, tO, e + 0.3)), x0 = x - L / 2 + L * 0.03, px = x0 + (L * 0.94) * cm / RULER_CM;
    g.fillStyle = paperTile('#a9c9d6', 2); g.fillRect(x0, y - 190, px - x0, 110);
    paperPiece([[-300, -60], [0, -60], [10, -20], [-6, 20], [8, 60], [-300, 60]], x0, y - 135, '#e3cf9b', { seed: 850, depth: 1.2 });
    paperPiece([[300, -60], [0, -60], [-10, -25], [6, 15], [-8, 60], [300, 60]], px, y - 135, '#dcc596', { seed: 851, depth: 1.2 });
    ruler(x, y, L, 0.005, { seed: 852, mark: cm, s: pop(t, A.at + 0.05, 0.4) });
    if (t > tL) {
      const yrs = RULER_CM / PL().ATLANTIC;
      tag(RULER_CM + ' cm at ' + fmtNum(PL().ATLANTIC) + ' cm a year: about ' + Math.round(yrs) + ' years', x, y + 150, 44, pop(t, tL, 0.35), { color: PAL.mustard, seed: 853 });
    }
    if (t > tG) label('your whole life so far: one ruler of ocean', x, y + 250, 42, tG, { seed: 854, color: PAL.navy, outline: 'rgba(251,245,230,0.95)' });
  }

  /* ======================================================================
     act: pangaea (line 23): the supercontinent splits (the video's real coastlines, a simplified sketch)
     ====================================================================== */
  const PGV = { x: 960, y: 420, w: 1360, h: 640, s: 4.4, lon0: -22, lat0: 8 };
  function actPangaea(S, t, A) {
    backdrop(COL.survey, 11);
    const v = PGV, V_ = window.GroundVideo, G = V_ && V_.geo ? V_.geo() : null;
    if (window.GroundLook) GroundLook.survey(v.x, v.y, v.w, v.h, { seed: 41, depth: 1.4, grid: false, contour: 0.3 });
    const tP = S.W(23, 'Pangaea'), tSp = S.W(23, 'split'), tTw = S.W(23, 'two'), tAgo = S.W(23, 'ago');
    const apart = E.inOutCubic(P(t, tSp, tAgo + 0.8)), together = 1 - apart;
    if (G && V_.PANGAEA) {
      g.save(); g.beginPath(); g.rect(v.x - v.w / 2 + 16, v.y - v.h / 2 + 16, v.w - 32, v.h - 32); g.clip();
      g.fillStyle = 'rgba(79,127,163,0.16)'; g.fillRect(v.x - v.w / 2, v.y - v.h / 2, v.w, v.h);
      g.translate(v.x - v.lon0 * v.s, v.y + v.lat0 * v.s); g.scale(v.s, v.s);
      g.lineJoin = 'round';
      g.fillStyle = paperTile('#dcc596', 5, 'tex_kraft'); g.fill(G.rest); g.lineWidth = 1.6 / v.s; g.strokeStyle = 'rgba(43,36,32,0.62)'; g.stroke(G.rest);
      [['SA', '#e3cf9b'], ['NA', '#e6d3a2']].forEach(([k, col]) => {
        if (!G.pieces[k].length) return;
        const m = V_.PANGAEA[k], p = together;
        g.save(); g.translate(m.px + m.dx * p, -(m.py + m.dy * p)); g.rotate(m.rot * p); g.translate(-m.px, m.py);
        const path = V_.path(k, G.pieces[k]);
        g.fillStyle = paperTile(col, 4, 'tex_kraft'); g.fill(path); g.lineWidth = 1.8 / v.s; g.strokeStyle = 'rgba(43,36,32,0.8)'; g.stroke(path);
        g.restore();
      });
      g.restore();
    } else {
      // no map data: two paper halves sliding apart
      paperRect(v.x - 160 - 260 * apart, v.y, 300, 380, '#e3cf9b', { seed: 870, torn: true, depth: 1.2 });
      paperRect(v.x + 160 + 260 * apart, v.y, 300, 380, '#dcc596', { seed: 871, torn: true, depth: 1.2 });
    }
    tag('Pangaea (a simplified sketch)', 960, 150, 48, pop(t, Math.min(tP, A.at + 0.3), 0.35), { color: PAL.mustard, seed: 880 });
    if (t > tTw) tag('started splitting about ' + PL().PANGAEA_SPLIT_MYR + ' million years ago', 960, 720, 40, pop(t, tTw, 0.35), { color: PAL.white, seed: 881 });
    if (t > tAgo + 0.9) label('and still going: the Atlantic keeps widening', 960, 790, 36, tAgo + 0.9, { seed: 882, color: PAL.navy, outline: 'rgba(251,245,230,0.95)' });
  }

  /* ---------------- the acts' plan (from the take's own line times) ---------------- */
  function plan(S) {
    const b = i => S.S(i) - 0.25;
    const acts = [
      { id: 'intro', at: 0, draw: actIntro },
      { id: 'plates', at: b(2), draw: actPlates },
      { id: 'chorus1', at: b(6), draw: actCycle, c0: 6 },
      { id: 'sand', at: b(10), draw: actSand },
      { id: 'chorus2', at: b(14), draw: actCycle, c0: 14 },
      { id: 'magma', at: b(18), draw: actMagma },
      { id: 'florida', at: b(20), draw: actFlorida },
      { id: 'ruler', at: S.W(22, 'ruler') - 0.5, draw: actRuler },          // after the sinkhole drops ("out of sight")
      { id: 'pangaea', at: S.S(23) + 0.1, draw: actPangaea },
      { id: 'finale', at: b(24), draw: actCycle, c0: 24, finale: true },
    ];
    acts.forEach((a, i) => { a.end = i + 1 < acts.length ? acts[i + 1].at : S.dur; });
    return { acts, ticks: acts.slice(1).map(a => a.at) };
  }
  function actAt(p, t) { let k = 0; for (let i = 0; i < p.acts.length; i++) if (t >= p.acts[i].at) k = i; return k; }

  /* torn-paper wipe (as Energy's rap): the old act torn away right → left, revealing the new one */
  function wipe(drawOld, drawNew, t, t0, dur, seed) {
    const p = E.inOutCubic(P(t, t0, t0 + dur));
    drawNew();
    if (p >= 1) return;
    const edge = tornEdgePts(lerp(1920 + 220, -260, p), seed);
    const poly = [[-100, -100], ...edge, [-100, 1180]];
    g.save(); shadowFill(poly, 2.2, PAL.paper); g.restore();
    g.save(); pathPts(poly); g.clip(); drawOld(); g.restore();
    const r = rng(seed + 1), inner = edge.map(q => [q[0] - 10 - r() * 14, q[1]]);
    g.save(); pathPts([...edge, ...inner.reverse()]); g.fillStyle = paperTile(PAL.white, 2); g.fill(); g.restore();
  }
  function drawActs(S, p, t) {
    const k = actAt(p, t), A = p.acts[k];
    const run = i => () => { g.save(); p.acts[i].draw(S, t, p.acts[i]); g.restore(); shadowOff(); g.globalAlpha = 1; };
    if (k > 0 && t < A.at + WIPE) wipe(run(k - 1), run(k), t, A.at, WIPE, 5 + k);
    else run(k)();
  }
  /* Leo (left) and Dr. Quinn (right): small cut-outs bobbing on the beat; they point on the hook words */
  function cast(S, t) {
    const pu = S.pulse(t, 7), bar = Math.floor(S.beatPos(t) / 4), sway = (bar % 2 ? 1 : -1) * 0.025;
    const near = (i, w) => { const x = S.W(i, w); return t > x && t < x + 1.1; };
    const hookLines = [6, 14, 24];
    const hook = hookLines.some(c => near(c, 'Melt') || near(c, 'squeeze') || near(c, 'break') || near(c + 3, 'Any')) || near(28, 'Any');
    const leoPt = hook || near(22, 'ruler') || near(22, 'lifetime') || near(3, 'ride');
    const quinnPt = hook || near(4, 'solid') || near(19, 'fast') || near(19, 'slow') || near(21, 'acid');
    Puppet.draw('leo', 150, 880, 300, { env: 0.15 + 0.85 * pu, facing: 1, t, pose: leoPt ? 'point' : 'cross', rot: sway });
    Puppet.draw('quinn', 1772, 880, 320, { env: 0.15 + 0.85 * pu, facing: -1, t, pose: quinnPt ? 'point' : 'quinn', rot: -sway });
  }

  /* ---------------- karaoke ---------------- */
  const GROUPS = [
    ['melt', 'it'], ['squeeze', 'it'], ['break', 'it', 'down'], ['deep', 'deep', 'time'],
    ['any', 'rock', 'can', 'be', 'any', 'kind'],
  ];
  const layoutCache = new Map();
  function tokens(item) {
    const ws = item.words, toks = [];
    for (let k = 0; k < ws.length; k++) {
      const grp = GROUPS.find(G => G.every((w, j) => ws[k + j] && normW(ws[k + j].w) === w));
      if (grp) {
        const run = ws.slice(k, k + grp.length), last = run[run.length - 1];
        const trail = (last.w.match(/[,.!?]+$/) || [''])[0];
        const text = run.map(w => w.w).join(' ').replace(/[,.!?]+$/, '').toUpperCase();   // ransom letters are caps
        toks.push({ text, s: run[0].s, e: last.e, ransom: true, trail, n: grp.length });
        k += grp.length - 1; continue;
      }
      toks.push({ text: ws[k].w, s: ws[k].s, e: ws[k].e });
    }
    return toks;
  }
  function layout(item, size) {
    const key = item.i + '|' + item.s + '|' + size;
    if (layoutCache.has(key)) return layoutCache.get(key);
    const toks = tokens(item);
    g.save(); g.font = `${size}px ${HANDF}`;
    const space = g.measureText(' ').width, rs = size * 0.6;
    let x = 0;
    toks.forEach((tk, j) => {
      tk.seed = 400 + item.i * 13 + j * 5;
      if (tk.ransom) {
        tk.w = ransom(tk.text, -9999, -9999, rs, 1e9, { seed: tk.seed });
        g.font = `${size}px ${HANDF}`;
        tk.tw = tk.trail ? g.measureText(tk.trail).width : 0;
        tk.x = x + size * 0.12; x += tk.w + tk.tw + size * 0.24 + space;
      } else { tk.w = g.measureText(tk.text).width; tk.x = x; x += tk.w + space; }
    });
    g.restore();
    const out = { toks, width: x - space, size, rs };
    layoutCache.set(key, out);
    return out;
  }
  function drawCurrent(item, t, y, maxW = 1780) {
    let size = CUR_SIZE, Lo = layout(item, size);
    if (Lo.width > maxW) { size = Math.floor(size * maxW / Lo.width); Lo = layout(item, size); }
    const x0 = 960 - Lo.width / 2;
    const appear = item.appear ?? item.s - 0.55, a = E.outCubic(P(t, appear, appear + 0.25));
    const yy = y + (1 - a) * 40;
    Lo.toks.forEach((tk, j) => {
      const nx = Lo.toks[j + 1], end = Math.min(Math.max(tk.e, tk.s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9);
      const x = x0 + tk.x, sung = t >= tk.s, active = t >= tk.s - 0.02 && t < end;
      if (tk.ransom) {
        if (!sung) hand(tk.text, x + tk.w / 2, yy, size * 0.7, -1e6, { dur: 0.01, alpha: 0.28 * a, seed: tk.seed, font: TYPE });
        else {
          const beat = 1 + 0.06 * Math.exp(-(t - tk.s) * 6);
          g.save(); g.translate(x + tk.w / 2, yy); g.scale(beat, beat);
          ransom(tk.text, 0, 0, Lo.rs, tk.s, { seed: tk.seed, stagger: clamp((tk.e - tk.s) / Math.max(1, tk.text.length), 0.02, 0.06) });
          g.restore();
        }
        if (tk.trail) hand(tk.trail, x + tk.w + 4, yy, size, sung ? -1e6 : 1e6, { dur: 0.01, align: 'left', seed: tk.seed + 1 });
        return;
      }
      if (active) {
        const bounce = 1 + 0.06 * Math.exp(-(t - tk.s) * 10);
        paperRect(x + tk.w / 2, yy + size * 0.06, (tk.w + 22) * bounce, size * 0.92, PAL.mustard, { seed: tk.seed, depth: 0.5, boil: false, alpha: 0.95 });
      }
      if (!sung || active) hand(tk.text, x, yy, size, -1e6, { dur: 0.01, align: 'left', alpha: 0.3 * a, seed: tk.seed });
      if (!sung) return;
      hand(tk.text, x, yy, size, tk.s, { dur: clamp(tk.e - tk.s, 0.08, 0.3), align: 'left', color: active ? PAL.red : PAL.ink, seed: tk.seed });
    });
  }
  function drawNext(item, y) {
    let size = NEXT_SIZE;
    g.save(); g.font = `${size}px ${HANDF}`; const w = g.measureText(item.text).width; g.restore();
    if (w > 1700) size = Math.floor(size * 1700 / w);
    hand(item.text, 960, y, size, -1e6, { dur: 0.01, alpha: 0.5, color: PAL.navy, seed: 90 + item.i });
  }
  /* a line comes up 0.55 s before its first word, but never before the previous line has been sung */
  const appearAt = (it, k) => (k === 0 ? it[0].s - 0.55 : Math.min(it[k].s, Math.max(it[k].s - 0.55, it[k - 1].e + 0.05)));
  function lyricAt(song, t) {
    const it = song.items;
    let cur = -1;
    for (let k = 0; k < it.length; k++) if (t >= appearAt(it, k)) cur = k;
    let c = cur >= 0 ? it[cur] : null;
    if (c) c.appear = appearAt(it, cur);
    const nx = it[cur + 1] || null;
    if (c && t > c.e + 2.2 && (!nx || nx.s - t > 1.2)) c = null;       // a long instrumental gap: clear the line
    return { cur: c, next: nx && nx.s - t < 8 ? nx : null };
  }
  /* the word being sung at t (QA: the karaoke's active word) */
  function activeWord(song, t) {
    const { cur } = lyricAt(song, t); if (!cur) return null;
    const ws = cur.words;
    for (let j = 0; j < ws.length; j++) { const nx = ws[j + 1], end = Math.min(Math.max(ws[j].e, ws[j].s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); if (t >= ws[j].s - 0.02 && t < end) return { line: cur.i, j, w: ws[j].w, s: ws[j].s, e: ws[j].e }; }
    return null;
  }
  const CREDIT = '(the song is AI-generated; the science is real)';
  function band(song, t) {
    paperRect(960, BAND_Y + 140, 2040, 290, PAL.cream, { seed: 77, torn: true, depth: 1.8, boil: false });
    tape(110, BAND_Y + 6, 150, -0.08, undefined, 71); tape(1810, BAND_Y + 6, 150, 0.08, undefined, 72);
    const { cur, next } = lyricAt(song, t);
    const sec = (cur || next || {}).section;
    if (sec) hand(sec.toLowerCase(), 70, BAND_Y + 34, 30, -1e6, { dur: 0.01, align: 'left', color: 'rgba(37,60,90,0.55)', seed: 73, font: TYPE });
    if (cur) drawCurrent(cur, t, CUR_Y);
    if (next) drawNext(next, NEXT_Y);
    else if (song.items.length && t > song.items[song.items.length - 1].e) hand(CREDIT, 960, NEXT_Y, 40, song.items[song.items.length - 1].e, { color: PAL.navy, alpha: 0.7, seed: 193 });
    if (!cur && !next && t > 1) {
      const b = ((Math.floor(song.beatPos(t)) % 3) + 3) % 3, pu = song.pulse(t, 6);
      ['♪', '♫', '♪'].forEach((n, i) => hand(n, 860 + i * 100, CUR_Y - 26 * pu * (i === b ? 1 : 0.15), 80, -1e6, { dur: 0.01, color: PAL.navy, seed: 80 + i, font: "'Segoe UI Symbol', 'DejaVu Sans', sans-serif" }));
    }
  }

  /* ======================================================================
     the scene
     ====================================================================== */
  class SongScene extends Scene {
    enter() {
      this.take = TAKE;
      this.song = null; this.plan = null; this.err = null;
      this.playing = false; this.frozen = false; this.ended = false; this.startedOnce = false;
      this.pos = 0; this.t0ctx = null; this.heard = 0;
      this.audioReady = false; this.completed = false; this.gone = false;
      Audio.stopMusic();
      const wurl = Assets.url(WORDS);
      const words = (window.Loader ? Loader.json(wurl).then(d => d || Promise.reject(new Error('no timings'))) : fetch(wurl).then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))))
        .then(d => { this.song = new Song(d); this.plan = plan(this.song); })
        .catch(e => { this.err = String(e && e.message); console.warn('[song] no timings', this.err); });
      const audio = (Audio.has(KEY) ? Promise.resolve() : Audio.load({ [KEY]: Assets.url(AUDIO) })).then(() => { this.audioReady = Audio.has(KEY); });
      Promise.all([words, audio]).then(() => { if (!this.gone && !this.frozen && !this.startedOnce && this.song) this.play(0); });
      if (typeof prewarm === 'function' && window.GroundVideo) prewarm(() => { GroundVideo.geo(); });
      if (window.Device) {
        this.onHidden = Device.on('hidden', () => { if (this.playing && !this.gone) { this.pause(); this.resumeOnReturn = true; } });
        this.onResume = Device.on('resume', () => { if (this.resumeOnReturn && !this.gone && Game.current() === this) { this.resumeOnReturn = false; this.play(this.pos); } });
      }
    }
    exit() {
      this.gone = true; Audio.stopGroup('song'); this.playing = false;
      if (this.media) this.media.pause();
      if (window.Device) { Device.off('hidden', this.onHidden); Device.off('resume', this.onResume); }
    }
    get key() { return KEY; }
    /* the song clock: the <audio> element while it plays (t0ctx != null = "on the audio clock"), else pos */
    now() {
      if (this.playing && this.t0ctx != null) {
        const m = this.media;
        if (m) return m.playing() ? m.time() : m.el && m.el.ended ? this.song.dur : this.pos;
        if (Audio.running()) return Audio.now() - this.t0ctx;
      }
      return this.pos;
    }
    play(from = this.now()) {
      if (!this.song) return;
      this.startedOnce = true; this.frozen = false; this.ended = false;
      from = clamp(from, 0, this.song.dur - 0.05);
      Audio.stopGroup('song');
      this.pos = from; this.playing = true; this.t0ctx = null;
      if (!this.audioReady || window.__timeScale > 1) return;
      this.media = this.media || Audio.media(KEY);
      if (this.media) {
        const m = this.media, tok = this.playTok = (this.playTok || 0) + 1;
        this.playAt = Game.time; this.blocked = false;
        m.play(from).then(ok => {
          if (tok !== this.playTok || this.gone) return;
          if (ok && this.playing) { this.pos = m.time(); this.t0ctx = 0; }
          else if (!ok) { this.pause(); this.blocked = true; }
        });
      } else if (Audio.running()) {
        const at = Audio.now() + 0.06;
        Audio.schedule(KEY, { at, offset: from, gain: 1, group: 'song', bus: 'voice', fade: 0.03 });
        this.t0ctx = at - from;
      }
    }
    pause() {
      this.pos = this.now(); this.playing = false; this.t0ctx = null; this.playTok = (this.playTok || 0) + 1;
      Audio.stopGroup('song');
      if (this.media) this.media.pause();
    }
    toggle() { if (this.playing) this.pause(); else this.play(this.ended ? 0 : this.pos); Audio.play('paper', { gain: 0.25 }); }
    seek(t) { if (!this.song) return; const was = this.playing; this.pause(); this.pos = clamp(t, 0, this.song.dur); this.ended = false; if (was) this.play(this.pos); }
    restart() { this.heard = 0; this.play(0); }
    /* QA: freeze on song time t (no pause overlay) */
    debugAt(t) { this.pause(); this.frozen = true; this.startedOnce = true; this.ended = false; this.pos = t; }
    /* stamp the step now (QA / integration; the first full listen does this by itself) */
    done() { if (this.completed) return; this.completed = true; if (!Game.progress.stamps[STEP]) Game.complete(STEP); }
    lyricAt(t) { return this.song ? lyricAt(this.song, t) : null; }
    activeWord(t) { return this.song ? activeWord(this.song, t) : null; }
    actAt(t) { return this.plan ? this.plan.acts[actAt(this.plan, t)].id : null; }

    update(dt) {
      if (!this.song || !this.playing || this.scrub) return;
      if (this.t0ctx == null) {
        this.pos += dt * (window.__timeScale || 1);
        const retry = this.audioReady && !(window.__timeScale > 1) &&
          (this.media ? !this.blocked && Game.time - (this.playAt || 0) > 3 : Audio.running());
        if (retry) this.play(this.pos);
      }
      this.heard += dt * (this.t0ctx == null ? (window.__timeScale || 1) : 1);
      if (this.now() >= this.song.dur - 0.05) {
        this.pause(); this.pos = this.song.dur; this.ended = true;
        if (!this.completed && this.heard > 0.85 * this.song.dur) this.done();
      }
    }
    keyDown(k) {
      if (!this.song) return;
      if (k === ' ') this.toggle();
      else if (k === 'r' || k === 'R') this.restart();
      else if (k === 'ArrowRight') this.seek(this.now() + 5);
      else if (k === 'ArrowLeft') this.seek(this.now() - 5);
      else if (k === 'Enter' && this.ended) Game.replace(new Levels.map());
    }
    buttons() {
      const r = UI.tap(80) / 2;
      return { r, y: Math.max(70, r + 10), x: [0, 1, 2].map(i => 1890 - r - i * (2 * r + 20)) };
    }
    stripZone() { return Math.max(32, UI.tap(32)); }
    pointerDown(x, y) {
      if (!this.song || this.ended) return;
      if (UI.docked()) { this.toggle(); return; }
      const B = this.buttons();
      if (y < B.y + B.r + 10 && x > B.x[2] - B.r - 10) return;
      if (y > 1080 - this.stripZone()) {
        this.scrub = { was: this.playing };
        if (this.playing) this.pause();
        this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); this.ended = false;
        return;
      }
      this.toggle();
    }
    pointerMove(x) { if (this.scrub && this.song) this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); }
    pointerUp() {
      if (!this.scrub) return;
      const was = this.scrub.was; this.scrub = null;
      if (was) this.play(this.pos);
    }

    draw() {
      const docked = UI.docked(), title = (Game.content.song && Game.content.song.title) || 'Deep Time';
      if (!this.song) {
        g.fillStyle = paperTile(PAL.kraft, 3, 'tex_kraft'); g.fillRect(0, 0, 1920, 1080);
        ransom(title.toUpperCase(), 960, 420, 110, this.enteredAt + 0.2, { seed: 42 });
        const msg = this.err ? 'the song timings are missing (' + this.err + ')' : 'tuning the guitars…';
        hand(msg, 960, 600, 54, this.enteredAt + 0.4, { color: PAL.navy, seed: 44 });
        if (docked) { Dock.head(title, ''); Dock.text('song-wait', [msg], { cls: 'note center' }); this.dockBar(); }
        else this.chrome(null);
        return;
      }
      const t = clamp(this.now(), 0, this.song.dur), gT = T, gBF = BF;
      T = t; BF = Math.floor(t * 8);
      try {
        drawActs(this.song, this.plan, t);
        cast(this.song, t);
        if (!docked) band(this.song, t);
      } finally { T = gT; BF = gBF; }
      if (docked) this.dock(t, title); else this.chrome(t);
    }

    /* ---------- the portrait dock: karaoke, controls and the strip below the stage ---------- */
    dock(t, title) {
      const S = this.song, pl = this.plan;
      const mmss = s => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
      Dock.head(title, `${mmss(t)} / ${mmss(S.dur)}`);
      const { cur, next } = lyricAt(S, t);
      const sec = (cur || next || {}).section;
      const lastE = S.items.length ? S.items[S.items.length - 1].e : 0;
      if (cur) {
        const key = cur.i + ':' + cur.s;
        if (!this._dk || this._dk.key !== key) {
          const hookIdx = new Set();
          tokens(cur).forEach(tk => { if (tk.ransom) cur.words.forEach((w, j) => { if (w.s >= tk.s && w.s <= tk.e) hookIdx.add(j); }); });
          this._dk = { key, words: cur.words.map((w, j) => ({ w: w.w, s: w.s, e: w.e, hook: hookIdx.has(j) })) };
        }
        Dock.karaoke({ key, words: this._dk.words, t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '' });
      } else Dock.karaoke({ key: 'gap', words: [], t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '',
        note: t > lastE && S.items.length ? 'the end ♪' : this.startedOnce || this.frozen ? '♪ ♫ ♪' : 'loading the song…' });
      Dock.text('credit', ['♪ AI-generated song (take ' + TAKE_NAME + ') · the science is real'], { cls: 'small center' });
      if (this.ended && Game.current() === this) {
        Dock.buttons('song-end', [{ label: '↺ Play it again', color: PAL.teal, onClick: () => this.restart() },
          { label: 'Back to the map', color: PAL.red, onClick: () => Game.replace(new Levels.map()) }]);
      }
      Dock.strip({ value: t, total: S.dur, ticks: pl.ticks, label: 'song position', onSeek: (v, phase) => this.scrubTo(v, phase) });
      this.dockBar();
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended && !this.scrub) {
        g.save(); g.fillStyle = 'rgba(27,21,18,0.28)'; g.fillRect(0, 0, 1920, 1080); g.restore();
        paperCircle(960, 540, 150, PAL.mustard, { seed: 63, depth: 1.6 });
        g.save(); g.fillStyle = PAL.ink; g.beginPath(); g.moveTo(912, 458); g.lineTo(1035, 540); g.lineTo(912, 622); g.closePath(); g.fill(); g.restore();
      }
    }
    dockBar() {
      if (this.song && !this.ended) {
        Dock.button({ id: 'restart', glyph: '↺', label: 'restart', aria: 'play the song from the start', onClick: () => this.restart() });
        Dock.button({ id: 'play', glyph: this.playing ? '❚❚' : '▶', label: this.playing ? 'pause' : 'play', aria: this.playing ? 'pause' : 'play', primary: true, onClick: () => this.toggle() });
      }
      Dock.button({ id: 'map', glyph: '⌂', label: 'map', aria: 'back to the map', onClick: () => Game.replace(new Levels.map()) });
    }
    scrubTo(v, phase) {
      if (!this.song) return;
      const pos = clamp(v, 0, this.song.dur - 0.05);
      if (phase === 'start') { this.scrub = { was: this.playing || (this.scrub && this.scrub.was) }; if (this.playing) this.pause(); this.ended = false; }
      this.pos = pos;
      if (phase === 'end') { const was = this.scrub ? this.scrub.was : false; this.scrub = null; if (was) this.play(this.pos); }
    }

    chrome(t) {
      const t0 = this.enteredAt;
      ransom('DEEP TIME', 40, 56, 34, t0 + 0.1, { align: 'left', seed: 61 });
      hand('♪ AI-generated song · take ' + TAKE_NAME + ' · the science is real', 44, 110, 26, t0 + 0.4, { align: 'left', color: PAL.cream, outline: OUTLINE, seed: 62 });
      const B = this.buttons(), fx = { r: B.r, fixed: true };
      if (UI.iconButton('song-map', '⌂', B.x[0], B.y, fx)) { Game.replace(new Levels.map()); return; }
      if (this.song) {
        if (UI.iconButton('song-restart', '↺', B.x[1], B.y, fx)) this.restart();
        if (UI.iconButton('song-play', this.playing ? '❚❚' : '▶', B.x[2], B.y, { ...fx, color: PAL.mustard })) this.toggle();
      }
      if (t == null) return;
      const S = this.song, pl = this.plan, zone = this.stripZone(), touch = zone > 32;
      if (!this.ended) UI.target('song-strip', 960, 1080 - zone / 2, 1920, zone);
      const sh = touch ? 22 : 12, sy = 1080 - sh;
      g.save();
      g.fillStyle = 'rgba(43,36,32,0.25)'; g.fillRect(0, sy, 1920, sh);
      g.fillStyle = PAL.red; g.fillRect(0, sy, 1920 * t / S.dur, sh);
      g.fillStyle = PAL.navy; pl.ticks.forEach(b => g.fillRect(1920 * b / S.dur - 2, sy - 6, 4, sh + 6));
      g.restore();
      if (touch || this.scrub) paperCircle(clamp(1920 * t / S.dur, 26, 1894), touch ? 1080 - 24 : sy + sh / 2, touch ? 22 : 14, PAL.navy, { seed: 66, depth: 0.8, boil: false });
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended) {
        g.save(); g.fillStyle = 'rgba(27,21,18,0.28)'; g.fillRect(0, 0, 1920, BAND_Y); g.restore();
        paperCircle(960, 430, 100, PAL.mustard, { seed: 63, depth: 1.6 });
        g.save(); g.fillStyle = PAL.ink; g.beginPath(); g.moveTo(928, 375); g.lineTo(1010, 430); g.lineTo(928, 485); g.closePath(); g.fill(); g.restore();
        hand(UI.hint('paused · click or Space to play', 'paused · tap to play'), 960, 590, 48, -1e6, { dur: 0.01, color: PAL.cream, outline: OUTLINE, seed: 64 });
      }
      if (!this.startedOnce && !this.frozen) hand('loading the song…', 960, 590, 48, this.enteredAt + 0.3, { color: PAL.cream, outline: OUTLINE, seed: 65 });
      if (this.ended && Game.current() === this) {
        if (UI.button('song-again', '↺ Play it again', 700, 560, { w: 440, h: 110, color: PAL.teal, size: 56 })) this.restart();
        if (UI.button('song-back', 'Back to the map', 1220, 560, { w: 440, h: 110, color: PAL.red, size: 56 })) Game.replace(new Levels.map());
      }
    }
  }
  SongScene.STEP = STEP;
  SongScene.TAKE = TAKE;
  SongScene.Song = Song;
  SongScene.plan = plan;
  Levels.song = SongScene;
})();
